import { ArrowLeft, Search, Book, Users, Trophy, Settings, Target } from 'lucide-react'

export default function HelpPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/images/sportspot-transparent-bg.png" 
                alt="Sportspot Logo" 
                className="h-8 sm:h-12 w-auto"
              />
            </div>
            <div>
              <a href="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Help Center</h1>
          
          {/* Search Bar */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for help articles..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#42a5f5] focus:border-transparent text-lg"
            />
          </div>

          {/* Help Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Target className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Getting Started</h3>
              </div>
              <p className="text-gray-700 mb-4">Learn the basics of spotting teams and building your collection.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• How to spot your first team</li>
                <li>• Understanding the dashboard</li>
                <li>• Creating your profile</li>
                <li>• Mobile app basics</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Trophy className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Competitions & Trips</h3>
              </div>
              <p className="text-gray-700 mb-4">Set up competitions and track your progress on trips.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Creating trip competitions</li>
                <li>• Inviting friends</li>
                <li>• Scoring and rules</li>
                <li>• Sharing results</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Book className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Teams & Leagues</h3>
              </div>
              <p className="text-gray-700 mb-4">Information about supported teams and leagues.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Supported leagues</li>
                <li>• Team identification</li>
                <li>• League updates</li>
                <li>• Missing teams</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Users className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Social Features</h3>
              </div>
              <p className="text-gray-700 mb-4">Connect with friends and share your achievements.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Adding friends</li>
                <li>• Sharing collections</li>
                <li>• Social challenges</li>
                <li>• Privacy settings</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Settings className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Account & Settings</h3>
              </div>
              <p className="text-gray-700 mb-4">Manage your account and customize your experience.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Account settings</li>
                <li>• Notification preferences</li>
                <li>• Privacy controls</li>
                <li>• Data export</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <Search className="h-8 w-8 text-[#42a5f5] mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Troubleshooting</h3>
              </div>
              <p className="text-gray-700 mb-4">Solutions to common problems and technical issues.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Login issues</li>
                <li>• Spotting not working</li>
                <li>• Performance problems</li>
                <li>• Bug reporting</li>
              </ul>
            </div>
          </div>

          {/* Popular Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Popular Articles</h2>
            <div className="space-y-4">
              <a href="#" className="block bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold text-gray-900 mb-2">How to spot your first team</h3>
                <p className="text-gray-600 text-sm">A step-by-step guide to getting started with Sportspot and making your first team spot.</p>
              </a>
              
              <a href="#" className="block bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold text-gray-900 mb-2">Creating and managing trip competitions</h3>
                <p className="text-gray-600 text-sm">Learn how to set up competitions with friends for your next vacation or road trip.</p>
              </a>
              
              <a href="#" className="block bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold text-gray-900 mb-2">Understanding the scoring system</h3>
                <p className="text-gray-600 text-sm">How points are calculated and what makes certain spots more valuable than others.</p>
              </a>
              
              <a href="#" className="block bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold text-gray-900 mb-2">What counts as a valid team spot?</h3>
                <p className="text-gray-600 text-sm">Guidelines on what merchandise and apparel qualifies for spotting.</p>
              </a>
              
              <a href="#" className="block bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold text-gray-900 mb-2">Troubleshooting login issues</h3>
                <p className="text-gray-600 text-sm">Common solutions for problems accessing your Sportspot account.</p>
              </a>
            </div>
          </section>

          {/* Contact Support */}
          <div className="bg-[#42a5f5] rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-semibold mb-4">Still need help?</h2>
            <p className="mb-6">Can't find what you're looking for? Our support team is here to help.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="bg-white text-[#42a5f5] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Contact Support
              </a>
              <a 
                href="/faq" 
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#42a5f5] transition-colors"
              >
                View FAQ
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
